import { createRoute, Link } from "@tanstack/react-router";
import { Route as TenantLayoutRoute } from "./_layout";
import { Badge } from "@/components/base/badges/badges";
import { Card, CardBody, CardHeader } from "@/components/application/card/card";
import { DataTable, type Column } from "@/components/application/data-table/data-table";
import { InlineAlert } from "@/components/application/alerts/inline-alert";
import { LoadingIndicator } from "@/components/application/loading-indicator/loading-indicator";
import { useTenantBudgetDetail, type TenantBudgetCardItem } from "../../hooks/useTenantBudgets";

/**
 * Detail rozpočtu — per-karta kalkulace (princip #3 transparentnost): jak vznikla
 * celková částka rozpadem na jednotlivé karty (počet transakcí + suma).
 */

function TenantBudgetDetailPage() {
  const { budgetId } = Route.useParams();
  const { data, isLoading, error } = useTenantBudgetDetail(budgetId);

  const columns: Column<TenantBudgetCardItem>[] = [
    {
      key: "card",
      header: "Karta",
      cell: (r) => (
        <div className="flex flex-col gap-0.5">
          <strong className="text-primary">{r.card_label ?? "Tankovací karta"}</strong>
          {r.card_number_display && <span className="text-xs text-tertiary">{r.card_number_display}</span>}
        </div>
      ),
    },
    { key: "holder", header: "Držitel", cell: (r) => r.holder_label ?? "nepřiřazeno" },
    { key: "tx", header: "Transakcí", cell: (r) => r.tx_count },
    { key: "sumkc", header: "Suma Kč", cell: (r) => `${r.sum_kc} Kč` },
    {
      key: "sumeur",
      header: "Suma €",
      cell: (r) => (r.sum_eur !== "0.00" ? `${r.sum_eur} €` : "—"),
    },
  ];

  return (
    <section aria-labelledby="budget-detail-title" className="max-w-7xl mx-auto space-y-6">
      <header className="flex flex-col gap-2">
        <Link to="/tenant/budgets" className="text-sm text-brand-secondary hover:underline w-fit">
          ← Zpět na rozpočty
        </Link>
        <div className="flex flex-wrap items-center gap-3">
          <h2 id="budget-detail-title" className="text-2xl font-bold text-primary m-0">
            {data ? `Rozpočet ${data.month.slice(0, 7)} — ${data.billing_group_code}` : "Detail rozpočtu"}
          </h2>
          {data && <Badge color={data.status === "approved" ? "success" : "warning"}>{data.status}</Badge>}
        </div>
      </header>

      {error && (
        <InlineAlert variant="error">
          {error instanceof Error ? error.message : "Načtení rozpočtu selhalo."}
        </InlineAlert>
      )}

      {data && (
        <Card>
          <CardBody>
            <div className="flex flex-wrap gap-6">
              <div>
                <div className="text-sm text-tertiary">Celkem Kč</div>
                <div className="text-2xl font-bold text-primary">{data.total_amount_kc} Kč</div>
              </div>
              {data.eur_advisory && (
                <div>
                  <div className="text-sm text-tertiary">Celkem €</div>
                  <div className="text-2xl font-bold text-primary">{data.total_amount_eur} €</div>
                </div>
              )}
            </div>
          </CardBody>
        </Card>
      )}

      <Card>
        <CardHeader>
          <h3 className="text-lg font-semibold text-primary m-0">Rozpis po kartách</h3>
        </CardHeader>
        <CardBody>
          {isLoading ? (
            <LoadingIndicator label="Načítání…" />
          ) : (
            <DataTable
              caption="Per-karta kalkulace rozpočtu"
              columns={columns}
              rows={data?.items ?? []}
              rowKey={(r) => r.fuel_card_id}
              emptyMessage="Žádné položky rozpočtu."
            />
          )}
        </CardBody>
      </Card>
    </section>
  );
}

export const Route = createRoute({
  getParentRoute: () => TenantLayoutRoute,
  path: "/tenant/budgets/$budgetId",
  component: TenantBudgetDetailPage,
});
